// hooks/useMenuData.ts
import jwt from 'jsonwebtoken'
import { useState, useEffect } from 'react'
import { MegaMenuItem } from '../components/Header/NewMegaMenuHeaderTabs'

// Create JWT token
async function getAuthToken(): Promise<string> {
  const response = await fetch('/api/auth/menuTokenGenerator  ', {
    method: 'POST',
  })
  
  if (!response.ok) {
    throw new Error('Failed to get auth token')
  }
  
  const { token } = await response.json()
  return token
}

// Your Cloud Function URL - replace with your actual URL
const CLOUD_FUNCTION_URL = process.env.NEXT_PUBLIC_MENU_API_URL ?? ''

/** Fetch menu data from Cloud Function */
async function fetchMenuData() {
  try {
    const response = await fetch(CLOUD_FUNCTION_URL, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch menu: ${response.statusText}`)
    }

    const data = await response.json()
    return { success: true, data }
  } catch (error: any) {
    console.error('Error fetching menu:', error)
    return { success: false, error: error.message }
  }
}

/** Update menu data in Cloud Function */
async function updateMenuData(newMenuData: { categories: Array<MegaMenuItem> }, userEmail: string) {
  try {
    const authToken = await getAuthToken()
    const response = await fetch(CLOUD_FUNCTION_URL, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
        'X-User-Email': userEmail,
      },
      body: JSON.stringify(newMenuData),
    })

    if (!response.ok) {
      throw new Error(`Failed to update menu: ${response.statusText}`)
    }

    const result = await response.json()
    return { success: true, data: result }
  } catch (error: any) {
    console.error('Error updating menu:', error)
    return { success: false, error: error.message }
  }
}

/** Custom hook to manage menu data Fetches menu from Cloud Function and provides save functionality */
export function useMenuData() {
  const [menuItems, setMenuItems] = useState<Array<MegaMenuItem>>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fetch menu data on mount
  useEffect(() => {
    loadMenu()
  }, [])

  const loadMenu = async () => {
    setLoading(true)
    setError(null)

    const result = await fetchMenuData()

    if (result.success) {
      setMenuItems(result.data.categories || [])
    } else {
      setError(result.error || 'Failed to load menu')
    }

    setLoading(false)
  }

  const saveMenu = async (newMenuData: { categories: Array<MegaMenuItem> }, userEmail: string) => {
    try {
      const result = await updateMenuData(newMenuData, userEmail)

      if (result.success) {
        // Reload from server to ensure consistency
        await loadMenu()
        return { success: true }
      }
      return { success: false, error: result.error || 'Failed to save menu' }
    } catch (error: any) {
      return { success: false, error: error.message }
    }
  }

  return {
    menuItems,
    loading,
    error,
    saveMenu,
    refetch: loadMenu,
  }
}
